/*
 * Occurra Button — mirrors the Figma design system Button component
 * (file kQs3kYj8njKAsjv55HP7N7, page 13:2485).
 *
 * Matrix:
 *   variant:  ghost | outlined | accent      (+ native HTML disabled)
 *   size:     mini | small | medium | large  (24 / 32 / 36 / 40 px tall)
 *   selected: bool                            (toggle/pressed state)
 *   selectedTone: neutral | brand             (the fill of the selected state)
 *   iconOnly: bool                            (square icon button)
 *
 * Spec mapping — height, label-only padding, radius, type, icon:
 *   - Mini   24px, px 10, rounded-sm (4px), text 12/16 normal, icon 12px
 *   - Small  32px, px 12, rounded-md (6px), text 14/20 medium, icon 16px
 *   - Medium 36px, px 14, rounded-md (6px), text 14/20 medium, icon 16px
 *   - Large  40px, px 16, rounded-lg (8px), text 14/20 medium, icon 16px
 *
 * The side a glyph sits on loses 2px of that padding: an icon reads as
 * lighter than a letter, so equal metrics look edge-heavy. Which side that is
 * comes from the children, not from a selector — a bare text label is not an
 * element, so CSS sees `<Icon/>Label` and `Label<Icon/>` as the same
 * only-child svg and would trim both sides of each.
 *
 * Icon-only locks the box square at the size's height, so the row of corner
 * controls every panel carries stays on one rhythm.
 *
 * Variant fills:
 *   ghost     → transparent / hover bg-occ-foreground-25 / selected bg-occ-foreground-50
 *   outlined  → border border-occ-foreground-100, transparent fill, same hover/selected as ghost
 *   accent    → bg-occ-foreground-high-contrast, text-occ-foreground-inverted
 *
 * Disabled (native HTML disabled): all variants render with text-occ-foreground-disabled,
 * no fill and no hover. Pointer events stay on, so the `disabledReason` tooltip opens.
 *
 * `tooltip` names the action of an icon-only button; `disabledReason` replaces it while the
 * button is disabled (docs/rules/interactive_elements.md, rules 6 and 7).
 */

import { cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { Hint } from './Hint';

const buttonVariants = cva(
    // Base — shared across all variants
    [
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap',
        'select-none outline-none',
        'focus-visible:ring-2 focus-visible:ring-occ-brand focus-visible:ring-offset-1',
        'focus-visible:ring-offset-occ-background-main',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    ].join(' '),
    {
        variants: {
            variant: {
                ghost: [
                    'bg-transparent text-occ-foreground-main',
                    // Default hover: subtle tint. A plain `hover:` lets a caller's `hover:bg-*`
                    // replace it; `disabled:bg-transparent` comes later in the CSS and wins.
                    'hover:bg-occ-foreground-25',
                    // Selected (toggled on)
                    'data-[selected=true]:not-disabled:bg-occ-foreground-50',
                    'data-[selected=true]:text-occ-foreground-high-contrast',
                    // Hover while selected: bump up one alpha step
                    'data-[selected=true]:not-disabled:hover:bg-occ-foreground-100',
                    'disabled:bg-transparent disabled:text-occ-foreground-disabled',
                ].join(' '),
                outlined: [
                    'border border-occ-foreground-100 bg-transparent text-occ-foreground-main',
                    'hover:bg-occ-foreground-25',
                    'data-[selected=true]:not-disabled:bg-occ-foreground-50',
                    'data-[selected=true]:text-occ-foreground-high-contrast',
                    'data-[selected=true]:not-disabled:hover:bg-occ-foreground-100',
                    'disabled:bg-transparent disabled:text-occ-foreground-disabled',
                    'disabled:border-occ-foreground-50',
                ].join(' '),
                accent: [
                    'bg-occ-foreground-high-contrast text-occ-foreground-inverted',
                    // The fill is already the darkest step in the light theme, so the hover
                    // lightens it to the 78% foreground: the press reads as "lifted".
                    'hover:bg-occ-foreground-main',
                    'disabled:bg-transparent disabled:text-occ-foreground-disabled',
                ].join(' '),
            },
            size: {
                // height, radius, typography, default svg size
                mini: "h-6 rounded-sm text-xs font-normal [&_svg:not([class*='size-'])]:size-3",
                small: "h-8 rounded-md text-sm font-medium [&_svg:not([class*='size-'])]:size-4",
                medium: "h-9 rounded-md text-sm font-medium [&_svg:not([class*='size-'])]:size-4",
                large: "h-10 rounded-lg text-sm font-medium [&_svg:not([class*='size-'])]:size-4",
            },
            iconOnly: {
                true: '',
                false: '',
            },
            selectedTone: {
                neutral: '',
                // The brand for the fill and the glyph, and a hover one tone along the ramp.
                brand: [
                    'data-[selected=true]:not-disabled:bg-occ-brand-400',
                    'data-[selected=true]:text-occ-brand',
                    'data-[selected=true]:not-disabled:hover:bg-occ-brand-500',
                ].join(' '),
            },
        },
        compoundVariants: [
            // Labelled buttons: symmetric padding, trimmed per side by GLYPH_TRIM.
            { iconOnly: false, size: 'mini', className: 'px-2.5' },
            { iconOnly: false, size: 'small', className: 'px-3' },
            { iconOnly: false, size: 'medium', className: 'px-3.5' },
            { iconOnly: false, size: 'large', className: 'px-4' },
            // Icon-only buttons are square — width locked to the size's height.
            { iconOnly: true, size: 'mini', className: 'w-6 px-0' },
            { iconOnly: true, size: 'small', className: 'w-8 px-0' },
            { iconOnly: true, size: 'medium', className: 'w-9 px-0' },
            { iconOnly: true, size: 'large', className: 'w-10 px-0' },
        ],
        defaultVariants: {
            variant: 'ghost',
            size: 'small',
            iconOnly: false,
            selectedTone: 'neutral',
        },
    },
);

type ButtonVariant = 'ghost' | 'outlined' | 'accent';
type ButtonSize = 'mini' | 'small' | 'medium' | 'large';
type ButtonSelectedTone = 'neutral' | 'brand';

interface ButtonProps extends React.ComponentProps<'button'> {
    variant?: ButtonVariant;
    size?: ButtonSize;
    /** Toggle / pressed state. */
    selected?: boolean;
    /** The fill of the selected state: the neutral tint, or the brand. */
    selectedTone?: ButtonSelectedTone;
    /** Square icon button with no label padding. */
    isIconOnly?: boolean;
    /** Renders the single child instead of a <button>, merging props into it. */
    asChild?: boolean;
    /** The action, in a tooltip. An icon-only button also takes it as its aria-label. */
    tooltip?: string;
    /** Why the button is disabled, in a tooltip while it is. */
    disabledReason?: string;
}

// The 2px a glyph gives back, per size. Tailwind emits `pl`/`pr` after `px`,
// so these win over the symmetric padding above without it being removed.
const GLYPH_TRIM: Record<ButtonSize, { left: string; right: string }> = {
    mini: { left: 'pl-2', right: 'pr-2' },
    small: { left: 'pl-2.5', right: 'pr-2.5' },
    medium: { left: 'pl-3', right: 'pr-3' },
    large: { left: 'pl-3.5', right: 'pr-3.5' },
};

/**
 * Does this child render as a glyph rather than as label text? Strings are
 * text; a host element is the caller wrapping their label (`<span>`), which
 * still wants the label's padding; anything else is an icon component.
 */
function isGlyph(child: React.ReactNode): boolean {
    if (!React.isValidElement(child)) {
        return false;
    }
    return typeof child.type !== 'string' || child.type === 'svg';
}

/** The trim classes for a button's leading and trailing edges. */
function getGlyphTrim(
    children: React.ReactNode,
    size: ButtonSize | undefined,
    isIconOnly: boolean,
): (string | false)[] | null {
    const trim = GLYPH_TRIM[size ?? 'small'];
    if (isIconOnly || !trim) {
        return null;
    }
    const items = React.Children.toArray(children);
    // A lone child is the whole button face — an icon-only button that did not
    // say so, or a label. Neither has a side to charge the trim to.
    if (items.length < 2) {
        return null;
    }
    return [
        isGlyph(items[0]) && trim.left,
        isGlyph(items[items.length - 1]) && trim.right,
    ];
}

export const Button = (allProps: ButtonProps): React.JSX.Element => {
    const {
        className,
        variant,
        size,
        isIconOnly = false,
        selected: isSelected = false,
        selectedTone,
        asChild = false,
        type,
        tooltip,
        disabledReason,
        children,
        ...props
    } = allProps;
    const Comp = asChild ? Slot.Root : 'button';
    // Default to type="button" when not in a Slot (avoids accidental form submits)
    const resolvedType = !asChild && type === undefined ? 'button' : type;

    const button = (
        <Comp
            type={resolvedType}
            data-slot="occ-button"
            data-variant={variant ?? 'ghost'}
            data-size={size ?? 'small'}
            data-selected={isSelected ? 'true' : undefined}
            aria-label={isIconOnly ? tooltip : undefined}
            className={cn(
                buttonVariants({ variant, size, iconOnly: isIconOnly, selectedTone }),
                getGlyphTrim(children, size, isIconOnly),
                className,
            )}
            {...props}
        >
            {children}
        </Comp>
    );
    // A call site that writes either prop keeps the tooltip mounted, also while the value is
    // undefined: a reason that comes and goes must not remount the button and drop its focus.
    if (!Object.hasOwn(allProps, 'tooltip') && !Object.hasOwn(allProps, 'disabledReason')) {
        return button;
    }
    return (
        <Hint label={props.disabled === true ? disabledReason ?? tooltip : tooltip}>
            {button}
        </Hint>
    );
};

export { buttonVariants };
